<div class="min-h-screen bg-gray-50">
  <!-- Header -->
  <app-header-nav></app-header-nav>

  <mat-toolbar class="border-b bg-white shadow-sm">
    <button mat-icon-button (click)="onBack()">
      <mat-icon>arrow_back</mat-icon>
    </button>
    <h1 class="text-xl font-semibold text-gray-800 ml-4">Analytics & Reports</h1>
    <span class="flex-auto"></span>
    <div class="flex gap-2 items-center">
      <mat-form-field class="w-36">
        <mat-label>Period</mat-label>
        <mat-select [(ngModel)]="selectedPeriod" (selectionChange)="onPeriodChange()">
          <mat-option value="DAILY">Today</mat-option>
          <mat-option value="WEEKLY">This Week</mat-option>
          <mat-option value="MONTHLY">This Month</mat-option>
          <mat-option value="YEARLY">This Year</mat-option>
        </mat-select>
      </mat-form-field>
      <button mat-stroked-button (click)="refreshData()" [disabled]="isLoading">
        <mat-icon>refresh</mat-icon>
        Refresh
      </button>
      <button mat-raised-button color="primary" (click)="exportReport()">
        <mat-icon>download</mat-icon>
        Export Report
      </button>
    </div>
  </mat-toolbar>

  <!-- Loading Overlay -->
  <div *ngIf="isLoading" class="flex items-center justify-center h-screen">
    <mat-spinner></mat-spinner>
  </div>

  <div class="p-6 space-y-6" [class.opacity-50]="isLoading">
    <!-- Overview Cards -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
      <mat-card>
        <mat-card-header class="flex flex-row items-center justify-between space-y-0 pb-2">
          <mat-card-title class="text-sm">Total Revenue</mat-card-title>
          <mat-icon class="text-green-500">trending_up</mat-icon>
        </mat-card-header>
        <mat-card-content>
          <div class="text-2xl font-bold text-green-500">${{ getTotalRevenue() }}</div>
          <p class="text-xs text-gray-600">+15% from last period</p>
        </mat-card-content>
      </mat-card>

      <mat-card>
        <mat-card-header class="flex flex-row items-center justify-between space-y-0 pb-2">
          <mat-card-title class="text-sm">Total Orders</mat-card-title>
          <mat-icon class="text-blue-500">receipt</mat-icon>
        </mat-card-header>
        <mat-card-content>
          <div class="text-2xl font-bold text-blue-500">{{ getTotalOrders() }}</div>
          <p class="text-xs text-gray-600">+8% from last period</p>
        </mat-card-content>
      </mat-card>

      <mat-card>
        <mat-card-header class="flex flex-row items-center justify-between space-y-0 pb-2">
          <mat-card-title class="text-sm">Avg Order Value</mat-card-title>
          <mat-icon class="text-purple-500">attach_money</mat-icon>
        </mat-card-header>
        <mat-card-content>
          <div class="text-2xl font-bold text-purple-500">${{ getAverageOrderValue().toFixed(2) }}</div>
          <p class="text-xs text-gray-600">+3% from last period</p>
        </mat-card-content>
      </mat-card>

      <mat-card>
        <mat-card-header class="flex flex-row items-center justify-between space-y-0 pb-2">
          <mat-card-title class="text-sm">Active Tables</mat-card-title>
          <mat-icon class="text-orange-500">table_restaurant</mat-icon>
        </mat-card-header>
        <mat-card-content>
          <div class="text-2xl font-bold text-orange-500">12</div>
          <p class="text-xs text-gray-600">Currently occupied</p>
        </mat-card-content>
      </mat-card>
    </div>

    <!-- Analytics Tabs with Charts -->
    <mat-tab-group>
      <mat-tab label="Sales Overview">
        <div class="mt-4">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- Revenue Chart -->
            <mat-card>
              <mat-card-header>
                <mat-card-title>Revenue by Hour</mat-card-title>
                <mat-card-subtitle>Today's hourly performance</mat-card-subtitle>
              </mat-card-header>
              <mat-card-content>
                <div class="space-y-2">
                  <div *ngFor="let hour of getRevenueByHour()" class="flex items-center justify-between">
                    <span class="text-sm text-gray-600">{{ hour.hour }}</span>
                    <div class="flex items-center gap-2">
                      <div class="w-32 bg-gray-200 rounded-full h-2">
                        <div class="bg-green-500 h-2 rounded-full"
                             [style.width.%]="(hour.revenue / 500) * 100"></div>
                      </div>
                      <span class="text-sm font-semibold">${{ hour.revenue }}</span>
                    </div>
                  </div>
                </div>
              </mat-card-content>
            </mat-card>

            <!-- Top Selling Drinks -->
            <mat-card>
              <mat-card-header>
                <mat-card-title>Top Selling Drinks</mat-card-title>
                <mat-card-subtitle>Best performers this {{ selectedPeriod }}</mat-card-subtitle>
              </mat-card-header>
              <mat-card-content>
                <div class="space-y-3">
                  <div *ngFor="let drink of getTopSellingDrinks()" class="flex items-center justify-between">
                    <div>
                      <span class="font-medium">{{ drink.name }}</span>
                      <span class="text-sm text-gray-500 ml-2">({{ drink.category }})</span>
                    </div>
                    <div class="text-right">
                      <div class="text-sm font-semibold">{{ drink.sales }} sold</div>
                      <div class="text-xs text-green-600">${{ drink.revenue.toFixed(2) }}</div>
                    </div>
                  </div>
                </div>
              </mat-card-content>
            </mat-card>
          </div>
        </div>
      </mat-tab>

      <mat-tab label="Category Performance">
        <div class="mt-4">
          <mat-card>
            <mat-card-header>
              <mat-card-title>Category Analysis</mat-card-title>
              <mat-card-subtitle>Performance by drink category</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content>
              <div class="space-y-4">
                <div *ngFor="let category of getCategoryPerformance()" class="space-y-2">
                  <div class="flex justify-between items-center">
                    <span class="font-medium">{{ category.category }}</span>
                    <span class="text-sm text-gray-600">${{ category.revenue.toFixed(2) }}</span>
                  </div>
                  <div class="w-full bg-gray-200 rounded-full h-3">
                    <div
                      class="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-500"
                      [style.width.%]="getCategoryPercentage(category.revenue)"></div>
                  </div>
                  <div class="text-xs text-gray-500">
                    {{ category.orders }} orders • {{ category.drinks }} drinks
                  </div>
                </div>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      </mat-tab>

      <mat-tab label="Staff Performance">
        <div class="mt-4">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- Staff Performance Chart -->
            <mat-card>
              <mat-card-header>
                <mat-card-title>Staff Performance Chart</mat-card-title>
                <mat-card-subtitle>Revenue generated by staff - {{ selectedPeriod }}</mat-card-subtitle>
              </mat-card-header>
              <mat-card-content>
                <div class="space-y-4">
                  <div *ngFor="let staff of getStaffPerformance()" class="space-y-2">
                    <div class="flex justify-between items-center">
                      <span class="font-medium">{{ staff.name }}</span>
                      <span class="text-sm text-gray-600">${{ staff.totalSales?.toFixed(2) || '0.00' }}</span>
                    </div>
                    <div class="w-full bg-gray-200 rounded-full h-3">
                      <div
                        class="bg-gradient-to-r from-green-500 to-blue-500 h-3 rounded-full transition-all duration-500"
                        [style.width.%]="getStaffPercentage(staff.totalSales)"></div>
                    </div>
                    <div class="text-xs text-gray-500">
                      {{ staff.ordersServed }} orders • ${{ staff.tipsReceived?.toFixed(2) || '0.00' }} tips • {{
                      staff.hoursWorked }}h worked
                    </div>
                  </div>
                </div>
              </mat-card-content>
            </mat-card>

            <!-- Top Performers -->
            <mat-card>
              <mat-card-header>
                <mat-card-title>Top Performers</mat-card-title>
                <mat-card-subtitle>Leading staff members this {{ selectedPeriod }}</mat-card-subtitle>
              </mat-card-header>
              <mat-card-content>
                <div class="space-y-4">
                  <!-- Highest Tips -->
                  <div class="bg-yellow-50 p-4 rounded-lg">
                    <h4 class="font-semibold text-yellow-800 mb-2">🏆 Highest Tips</h4>
                    <div *ngFor="let staff of getTopPerformers('tips').slice(0, 3); let i = index"
                      class="flex items-center justify-between py-2">
                      <div class="flex items-center gap-2">
                        <span
                          class="w-6 h-6 bg-yellow-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                          {{ i + 1 }}
                        </span>
                        <span class="font-medium">{{ staff.name }}</span>
                      </div>
                      <span class="text-yellow-600 font-semibold">${{ staff.tipsReceived || '0.00' }}</span>
                    </div>
                  </div>

                  <!-- Most Orders Served -->
                  <div class="bg-blue-50 p-4 rounded-lg">
                    <h4 class="font-semibold text-blue-800 mb-2">📋 Most Orders Served</h4>
                    <div *ngFor="let staff of getTopPerformers('orders').slice(0, 3); let i = index"
                      class="flex items-center justify-between py-2">
                      <div class="flex items-center gap-2">
                        <span
                          class="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                          {{ i + 1 }}
                        </span>
                        <span class="font-medium">{{ staff.name }}</span>
                      </div>
                      <span class="text-blue-600 font-semibold">{{ staff.ordersServed }} orders ({{
                        staff.ordersPerHour?.toFixed(1) || '0.0' }}/h)</span>
                    </div>
                  </div>

                  <!-- Most Hours Worked -->
                  <div class="bg-green-50 p-4 rounded-lg">
                    <h4 class="font-semibold text-green-800 mb-2">⏰ Most Hours Worked</h4>
                    <div *ngFor="let staff of getTopPerformers('hours').slice(0, 3); let i = index"
                      class="flex items-center justify-between py-2">
                      <div class="flex items-center gap-2">
                        <span
                          class="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                          {{ i + 1 }}
                        </span>
                        <span class="font-medium">{{ staff.name }}</span>
                      </div>
                      <span class="text-green-600 font-semibold">{{ staff.hoursWorked || 0 }}h</span>
                    </div>
                  </div>
                </div>
              </mat-card-content>
            </mat-card>
          </div>
        </div>
      </mat-tab>

      <mat-tab label="Recent Activity">
        <div class="mt-4">
          <mat-card>
            <mat-card-header>
              <mat-card-title>Recent Activity</mat-card-title>
              <mat-card-subtitle>Latest events and transactions</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content>
              <div class="space-y-3">
                <div *ngFor="let activity of getRecentActivity()"
                  class="flex items-center justify-between py-3 border-b last:border-b-0">
                  <div class="flex items-center gap-3">
                    <div class="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                      <mat-icon [class]="activity.color">{{ activity.icon }}</mat-icon>
                    </div>
                    <div>
                      <p class="text-sm font-medium">{{ activity.description }}</p>
                      <p class="text-xs text-gray-600">{{ activity.time }}</p>
                    </div>
                  </div>
                  <div *ngIf="activity.amount" class="text-right">
                    <span class="text-sm font-semibold text-green-500">${{ activity.amount.toFixed(2) }}</span>
                  </div>
                </div>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      </mat-tab>
    </mat-tab-group>
  </div>

  <!-- Footer -->
  <app-footer></app-footer>
</div>